import { skipToken } from '@apollo/client/react'
import { parseArtworkUrl } from 'pixai-utils/url'
import { useTranslation } from 'react-i18next'
import { Navigate, ScrollRestoration } from 'react-router'
import { withoutTrailingSlash } from 'ufo'
import ArtworkDetail from 'src/components/ArtworkDetail/ArtworkDetail'
import Empty from 'src/components/Empty'
import LoadingPage from 'src/components/LoadingPage'
import { useRenderTracker } from 'src/features/RenderTracker/useRenderTracker'
import { useGetArtworkWithTaskDetail } from 'src/services/apollo/artwork'
import type { ArtworkWithTaskDetailBaseFragment } from 'src/services/apollo/generated/graphql'
import {
  getArtworkLink,
  getArtworkTitle,
  isAnimatedArtwork,
} from 'src/utils/artwork'
import { getErrorMessage } from 'src/utils/error'
import { getErrorCode } from 'src/utils/errorUtil'
import { useLocation } from 'src/utils/router/route'
import { slugify } from 'src/utils/url'

/**
 * Shared page body for both CSR and Full SSR entries.
 *
 * - CSR entry (`clientRoutes/_app.artwork.$idSlug.csr.tsx`): renders
 *   without `initialArtwork`, falls back to client-side
 *   `useGetArtworkWithTaskDetail` Apollo query.
 * - Full SSR entry (`clientRoutes/_app.artwork.$idSlug.full-ssr.tsx`):
 *   passes `initialArtwork` from route loader's `artworkApollo.artworkData`
 *   so the first paint shows real content without waiting on hydrate.
 */
const ArtworkDetailPage = (props: {
  initialArtwork?: ArtworkWithTaskDetailBaseFragment
}) => {
  const { t } = useTranslation(['common', 'artwork'])
  const location = useLocation()
  const {
    id,
    slug,
    type: artworkType,
  } = parseArtworkUrl(withoutTrailingSlash(location.pathname)) ?? {}

  const {
    data: fetchedArtwork,
    loading,
    error,
  } = useGetArtworkWithTaskDetail(id ? { id } : skipToken)
  const resolvedArtwork = fetchedArtwork ?? props.initialArtwork

  useRenderTracker({
    pageName: 'artwork-detail',
    ready:
      !loading &&
      typeof resolvedArtwork === 'object' &&
      resolvedArtwork !== null,
  })

  const resolvedArtworkTitle =
    resolvedArtwork && slugify(getArtworkTitle(resolvedArtwork))

  const errMsg = error
    ? getErrorCode(error)
      ? getErrorMessage(t, {
          errName: getErrorCode(error),
          i18nKeyPrefix: 'artwork:',
        })
      : error.message
    : 'Artwork does not exist'

  if (resolvedArtwork && isAnimatedArtwork(resolvedArtwork)) {
    const videoPageUrl = getArtworkLink(resolvedArtwork)
    if (!location.pathname.startsWith(videoPageUrl)) {
      return <Navigate to={getArtworkLink(resolvedArtwork)} replace />
    }
  }

  return (
    <>
      {resolvedArtwork ? (
        slug && resolvedArtworkTitle ? (
          // Redirect to ID-only URL if slug exists
          <Navigate to={`/${artworkType}/${resolvedArtwork.id}`} replace />
        ) : (
          <>
            {/*
              JSON-LD for artwork detail is emitted in route `meta()` (see
              src/routes/_app.artwork.$idSlug.tsx) so it lands in <head> during
              the first SSR flush without depending on Apollo hydration.
            */}
            <ArtworkDetail
              artwork={resolvedArtwork}
              artworkLoras={resolvedArtwork.loras}
              taskParameters={resolvedArtwork.taskParameters}
              isSinglePage
            />
            <ScrollRestoration />
          </>
        )
      ) : loading ? (
        <LoadingPage fullscreen />
      ) : (
        <Empty description={errMsg} />
      )}
    </>
  )
}

export const Component = ArtworkDetailPage
